import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

/* Dupe Order — step 1, V1's "Copying Order" form 1 (`lib_editrecord.pl:21652-
 * 21739`, transcribed in docs/plans/2026-08-13-dupe-order-v1-rules.md §2).
 *
 * PURE UI, built on the CreateOrderModal recipe: `useModalDrag` above the early
 * return (React #310), `ModalHeader`, backdrop target-equality close, full reset
 * on every open, shared input/label classes. It reads nothing and posts nothing
 * — `OrderDupeActions` owns the flow and the API call.
 *
 * Deliberate deviation from V1's form: the count is a numeric input rather than
 * a 199-option RESTRICTED combobox (same 1..199 clamp, typed instead of
 * scrolled), and the range violation is an inline error rather than an
 * `alert()`.
 */

/** The six values V1 posts from form 1 — the flags half of the dupe payload. */
export interface DupeOrderFlags {
  dupecount: number;
  samemaster: 'Y' | 'N';
  copymrbl: 'Y' | 'N';
  copynotes: 'Y' | 'N';
  copyaccessorials: 'Y' | 'N';
  copydocuments: 'Y' | 'N';
}

/** V1's client-side cap (v1-rules §2); the backend enforces the same 1..199. */
export const MAX_DUPE_COUNT = 199;

export interface DupeOrderModalProps {
  open: boolean;
  /** Source order being copied — titles the dialog. */
  sourcePro: number;
  onClose: () => void;
  onContinue: (opts: DupeOrderFlags) => void;
}

type YN = 'Y' | 'N';

const DupeOrderModal: React.FC<DupeOrderModalProps> = ({
  open,
  sourcePro,
  onClose,
  onContinue,
}) => {
  // Held as a string so the field can be emptied mid-edit; validated on Continue.
  const [dupecount, setDupecount] = useState('1');
  const [samemaster, setSamemaster] = useState<YN>('Y');
  const [copymrbl, setCopymrbl] = useState<YN>('Y');
  const [copynotes, setCopynotes] = useState<YN>('Y');
  const [copyaccessorials, setCopyaccessorials] = useState<YN>('Y');
  const [copydocuments, setCopydocuments] = useState<YN>('Y');
  const [error, setError] = useState<string | null>(null);

  const countRef = useRef<HTMLInputElement>(null);

  // Fresh dialog on every open — a previous attempt's count/flags must never
  // carry over (the same rule CreateOrderModal applies to its fields). V1's
  // defaults: count 1, samemaster Y, all four copy flags Y.
  useEffect(() => {
    if (!open) return;
    setDupecount('1');
    setSamemaster('Y');
    setCopymrbl('Y');
    setCopynotes('Y');
    setCopyaccessorials('Y');
    setCopydocuments('Y');
    setError(null);
    // V1 focuses + selects the count so typing replaces the 1 (`:21694`).
    countRef.current?.focus();
    countRef.current?.select();
  }, [open]);

  // Hooks must run on every render — this sits ABOVE the early return (React #310).
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const handleContinue = () => {
    const n = Number((dupecount || '').trim());
    if (!Number.isInteger(n) || n < 1 || n > MAX_DUPE_COUNT) {
      setError(`Enter a number of new pro numbers between 1 and ${MAX_DUPE_COUNT}.`);
      return;
    }
    setError(null);
    onContinue({
      dupecount: n,
      samemaster,
      copymrbl,
      copynotes,
      copyaccessorials,
      copydocuments,
    });
  };

  const inputCls = 'border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  const labelCls = 'block text-sm font-medium text-label mb-1';

  /** The four copy flags + samemaster are all the same Y/N select. */
  const ynSelect = (
    id: string,
    label: string,
    value: YN,
    onChange: (next: YN) => void,
  ) => (
    <div>
      <label className={labelCls} htmlFor={id}>{label}</label>
      <select
        id={id}
        aria-label={label}
        className={`${inputCls} w-full`}
        value={value}
        onChange={(e) => onChange(e.target.value as YN)}
      >
        <option value="Y">Y</option>
        <option value="N">N</option>
      </select>
    </div>
  );

  return createPortal(
    <div
      data-testid="dupe-flags-backdrop"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-label="Copying Order"
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md border border-slate-200 dark:border-slate-700"
      >
        <ModalHeader
          title={`Copying Order - ${sourcePro}`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 py-3 flex flex-col gap-3">
          <div>
            <label className={labelCls} htmlFor="dupe-order-count">
              How many more pro numbers?
            </label>
            <input
              id="dupe-order-count"
              ref={countRef}
              aria-label="How many more pro numbers?"
              type="number"
              min={1}
              max={MAX_DUPE_COUNT}
              step={1}
              className={`${inputCls} w-full`}
              value={dupecount}
              onChange={(e) => { setDupecount(e.target.value); setError(null); }}
            />
          </div>

          {ynSelect(
            'dupe-order-samemaster',
            'Put new orders on this same Master Pro (same invoice)?',
            samemaster,
            setSamemaster,
          )}
          {ynSelect('dupe-order-copymrbl', 'Copy MRs/BLs/Whse Instructions?', copymrbl, setCopymrbl)}
          {ynSelect('dupe-order-copynotes', 'Copy Notes?', copynotes, setCopynotes)}
          {ynSelect('dupe-order-copyaccessorials', 'Copy Accessorials?', copyaccessorials, setCopyaccessorials)}
          {ynSelect('dupe-order-copydocuments', 'Copy Documents?', copydocuments, setCopydocuments)}

          {error && (
            <div role="alert" className="text-[0.75rem] text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded px-2 py-1.5">
              {error}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700"
          >
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DupeOrderModal;
